import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { subcategorySlug } from '../categorySlug';
import { formatNameWithCount } from '../categoryLabels';
import {
  categorySearchPath,
  subcategoryBrowsePath,
} from '../categoryPaths';
import {
  breadcrumbJsonLd,
  categoryIntro,
  categoryMetaDescription,
  categoryPageTitle,
} from '../seo';
import { shouldSubmitSearch } from '../searchSubmit';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { LogoMark } from './LogoMark';
import { CategoryMenuDropdown } from './CategoryMenuDropdown';
import { SearchField } from './SearchField';
import { CategoryProductGrid } from './CategoryList';
import {
  CategoryProductGridSkeleton,
  landingProductSkeletonCount,
} from './ProductCardSkeleton';
import { GetAlertsButton } from './GetAlertsButton';
import { SearchAlertSignupModal } from './SearchAlertSignupModal';
import { SearchResultsPage } from './SearchResultsPage';
import { SiteFooter } from './SiteFooter';
import {
  buildCategoryHubAlertScope,
  buildSubcategoryAlertScope,
} from '../searchAlertScope';
import { PageHead } from './PageHead';
import { trackOpenSignupModal, trackSelectSubcategory } from '../ga';
import './LandingPage.css';
import './SearchResultsPage.css';
import './CategoryPage.css';

const LAZY_LOAD_ROOT_MARGIN = '400px 0px';

function LazySubcategorySection({ categorySlug, categoryName, subcategory, onAlertsClick }) {
  const sectionRef = useRef(null);
  const fetchStartedRef = useRef(false);
  const [products, setProducts] = useState(null);
  const countNum = Number(subcategory.count);
  const needsProducts = Number.isFinite(countNum) && countNum > 0;
  const skeletonCount = landingProductSkeletonCount(subcategory.count);
  const subSlug = subcategorySlug(subcategory);
  const browseHref = subcategoryBrowsePath(categorySlug, subSlug);

  useEffect(() => {
    if (!needsProducts) {
      return undefined;
    }

    const el = sectionRef.current;
    if (!el) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || fetchStartedRef.current) {
          return;
        }
        fetchStartedRef.current = true;

        const url = new URL('/api/products.php', window.location.origin);
        url.searchParams.set('q', subcategory.search);
        url.searchParams.set('shop', categorySlug);
        url.searchParams.set('preview', '1');

        fetch(url.toString())
          .then((res) => jsonOrThrow(res))
          .then((data) => {
            const products = Array.isArray(data?.products) ? data.products : [];
            setProducts(products);
          })
          .catch(() => {
            setProducts([]);
          });
      },
      { rootMargin: LAZY_LOAD_ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [categorySlug, needsProducts, subcategory.search]);

  const showSkeleton = needsProducts && products === null;

  return (
    <section
      ref={sectionRef}
      id={subSlug}
      className="landing-categories__section"
    >
      <header className="landing-categories__section-header">
        <h2 className="landing-categories__heading">
          <Link
            className="landing-categories__heading-link"
            to={browseHref}
            onClick={() =>
              trackSelectSubcategory(categorySlug, subcategory.name, 'category_hub')
            }
          >
            {formatNameWithCount(subcategory.name, subcategory.count)}
          </Link>
        </h2>
        {onAlertsClick && (
          <GetAlertsButton
            variant="text"
            onClick={() =>
              onAlertsClick(
                buildSubcategoryAlertScope(categorySlug, categoryName, subcategory),
              )
            }
            label={`Get alerts for ${subcategory.name}`}
          />
        )}
      </header>
      {showSkeleton ? (
        <CategoryProductGridSkeleton count={skeletonCount} />
      ) : needsProducts ? (
        <CategoryProductGrid
          name={subcategory.name}
          count={subcategory.count}
          seeAllHref={browseHref}
          products={products}
          outboundCampaign="category"
          categorySlug={categorySlug}
          seeAllSourcePage="category_hub"
          browseFacebookContext={subcategory.name}
          browseFacebookLocation="category_hub_subcategory"
        />
      ) : (
        <p className="landing-categories__empty">Coming soon.</p>
      )}
    </section>
  );
}

export function CategoryPage() {
  const { slug: rawSlug } = useParams();
  const slug = (rawSlug ?? '').toLowerCase();
  const navigate = useNavigate();

  const [browseCategories, setBrowseCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [draftQuery, setDraftQuery] = useState('');
  const [searchAlertModal, setSearchAlertModal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setCategory(null);

    Promise.all([
      fetch('/api/landing-categories.php').then((r) => jsonOrThrow(r)),
      fetch('/api/browse-websites.php').then((r) => jsonOrThrow(r)),
    ])
      .then(([landingData, siteData]) => {
        if (cancelled) {
          return;
        }
        const categories = Array.isArray(landingData?.categories)
          ? landingData.categories
          : [];
        const match = categories.find((c) => c.slug === slug) ?? null;
        setCategory(match);
        setBrowseCategories(
          Array.isArray(siteData?.categories) ? siteData.categories : [],
        );
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(describeLoadFailure(e));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  function applySearch(query) {
    const trimmed = query.trim();
    if (!shouldSubmitSearch(trimmed, { shopSlug: slug })) {
      return;
    }
    navigate(categorySearchPath(trimmed, slug));
  }

  const subcategories = Array.isArray(category?.subcategories)
    ? category.subcategories
    : [];

  const categoryName =
    typeof category?.name === 'string' ? category.name.trim() : '';

  function openSearchAlert(scope, alertScope = 'category') {
    trackOpenSignupModal('search_alert', alertScope);
    setSearchAlertModal(scope);
  }

  const canonicalPath = category ? `/shop/${slug}` : undefined;
  const seoJsonLd = useMemo(() => {
    if (!categoryName || !canonicalPath) {
      return undefined;
    }
    return breadcrumbJsonLd([
      { name: 'Home', url: '/' },
      { name: categoryName, url: canonicalPath },
    ]);
  }, [categoryName, canonicalPath]);

  const embeddedSearch = useMemo(() => {
    if (!category || !categoryName) {
      return null;
    }
    return {
      shopCategorySlug: slug,
      effectiveQuery: '',
      browseLabel: categoryName,
    };
  }, [category, categoryName, slug]);

  return (
    <div className="results-page category-page">
      {categoryName && canonicalPath && (
        <PageHead
          title={categoryPageTitle(categoryName)}
          description={categoryMetaDescription(categoryName)}
          canonicalPath={canonicalPath}
          jsonLd={seoJsonLd}
        />
      )}
      <header className="results-page__header">
        <div className="results-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <CategoryMenuDropdown
              browseCategories={browseCategories}
              activeShopCategorySlug={slug}
              disabled={loading}
            />
            <HeaderMoreMenu />
          </div>
          <div className="results-page__header-center">
            <SearchField
              id="category-search"
              className="results-page__search"
              value={draftQuery}
              onChange={setDraftQuery}
              onSubmitSearch={applySearch}
              searchSubmitScope={{ shopSlug: slug, subcategory: null }}
              placeholder="Search in this category…"
              gaSearchLocation="category_page"
              scopePill={
                category?.name
                  ? {
                      label: category.name,
                      onRemove: () => navigate('/'),
                    }
                  : null
              }
            />
          </div>
          <nav
            className="results-page__header-nav"
            aria-label="Site sections"
          >
            <Link className="results-page__nav-link" to="/about">
              About
            </Link>
            <Link className="results-page__nav-link" to="/add-store">
              Add your store
            </Link>
          </nav>
        </div>
      </header>

      <div className="category-page__main">
        {loading && (
          <p className="category-page__status" aria-live="polite">
            Loading category…
          </p>
        )}
        {!loading && loadError && (
          <p className="category-page__status category-page__status--error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && category == null && (
          <div className="category-page__not-found">
            <p className="category-page__status">Category not found.</p>
            <Link className="category-page__back" to="/">
              Back to home
            </Link>
          </div>
        )}
        {!loading && !loadError && category != null && (
          <>
            <nav className="category-page__breadcrumbs" aria-label="Breadcrumb">
              <Link to="/" className="category-page__crumb category-page__crumb--link">
                Home
              </Link>
              <span className="category-page__sep" aria-hidden="true">
                ›
              </span>
              <span
                className="category-page__crumb category-page__crumb--current"
                aria-current="page"
              >
                {category.name}
              </span>
              <GetAlertsButton
                variant="toolbar"
                className="category-page__alerts-btn"
                onClick={() =>
                  openSearchAlert(
                    buildCategoryHubAlertScope(slug, categoryName),
                    'category',
                  )
                }
                label={`Get alerts for ${categoryName}`}
              />
            </nav>
            <h1 className="category-page__title">{categoryName} in Malta</h1>
            <p className="category-page__intro">{categoryIntro(categoryName)}</p>
            {subcategories.length === 0 ? (
              <p className="category-page__status">No subcategories to show.</p>
            ) : (
              <div
                className="landing-categories category-page__sections"
                aria-label={`${category.name} subcategories`}
              >
                {subcategories.map((sub) => (
                  <LazySubcategorySection
                    key={sub.name}
                    categorySlug={slug}
                    categoryName={categoryName}
                    subcategory={sub}
                    onAlertsClick={(scope) => openSearchAlert(scope, 'subcategory')}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {embeddedSearch && (
        <SearchResultsPage embedded={embeddedSearch} />
      )}

      <SiteFooter
        browseCategories={browseCategories}
        activeShopCategorySlug={slug}
      />
      {searchAlertModal != null && (
        <SearchAlertSignupModal
          onClose={() => setSearchAlertModal(null)}
          searchPath={searchAlertModal.searchPath}
          scopeLabel={searchAlertModal.scopeLabel}
          scopeChips={searchAlertModal.chips}
        />
      )}
    </div>
  );
}
